import { useEffect, useState } from "react";
import {
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Card,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  Table,
  TableCell,
  TableRow,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { FormStyleCollection as formStyles } from "../../styles/form";
import { AddCircle, Delete, ContentCopy } from "@mui/icons-material";
import { isEmpty } from "../../utils/Util";
import { useGetOnboardedDealersByMerchantQuery } from "../../services/API/surchargeApiSlice";
import {
  usePerformWebHookHealthCheckMutation,
  useCreateWebHookSecretMutation,
  useUpdatedWebHookSubscriberMutation,
  useGetWebhookConfigurationByIdQuery,
} from "../../services/API/webHookApiSlice";
import {
  ModifyWebHookSubscriberRequest,
  CreateWebHookSecretRequest,
  performHealthCheckRequest,
} from "../../types/WebHookTypes";
import { toast } from "react-toastify";
import LockIcon from "@mui/icons-material/Lock";
import { useNavigate, Navigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import {
  validateUrl,
  copyText,
  handleApiError,
  useCommonKVP,
  ALL_PAYMENT_TYPES,
  PAYMENT_TYPE_OPTIONS,
} from "./webhook.common";
import { useFlags } from "launchdarkly-react-client-sdk";

export default function ModifyWebHook() {
  const location = useLocation();
  const navigate = useNavigate();
  const subscriberId = location?.state?.id;
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSecretProcessing, setIsSecretProcessing] = useState(false);
  const [isHealthCheckProcessing, setIsHealthCheckProcessing] = useState(false);

  const [formData, setFormData] =
    useState<ModifyWebHookSubscriberRequest>(null);
  const [secretData, setSecretData] =
    useState<CreateWebHookSecretRequest>(null);

  const [dealersList, setDealersList] = useState<any>([]);
  const [selectedDealers, setSelectedDealers] = useState<any>([]);
  const [isDealerDisabled, setIsDealerDisabled] = useState(true);
  // Subscribers are created unfiltered, so ALL is the starting point until one is applied here.
  const [selectedPaymentTypes, setSelectedPaymentTypes] = useState<string[]>([
    ALL_PAYMENT_TYPES,
  ]);

  const [isValidEndPoint, setIsValidEndPoint] = useState<any>(false);
  const [isValidDeadUrl, setIsValidDeadUrl] = useState<any>(false);
  const [emailError, setEmailError] = useState<any>(false);

  const [healthCheckId, setHealthCheckId] = useState<any>(null);
  const [healthCheckedURL, setHealthCheckedURL] = useState<any>(null);

  const [secretId, setSecretId] = useState<any>(null);
  const [secretCode, setSecretCode] = useState<any>("");

  const [performHealthCheck] = usePerformWebHookHealthCheckMutation();
  const [createSecret] = useCreateWebHookSecretMutation();
  const [submitForm] = useUpdatedWebHookSubscriberMutation();

  const {
    rows,
    handleKVPChange,
    handleAddRow,
    handleRemoveRow,
    handleHeaderParse,
  } = useCommonKVP();

  const flags = useFlags();
  const allowWebhookIntegration = flags.allowWebhookIntegrations;

  const { data: subscriberData, isFetching: isSubscriberFetching } =
    useGetWebhookConfigurationByIdQuery(subscriberId, {
      skip: !subscriberId,
      refetchOnMountOrArgChange: true,
    });


  const { data: dealersByMerchantData, isFetching } = useGetOnboardedDealersByMerchantQuery(
    subscriberData?.merchantId,
    { skip: !subscriberData?.merchantId, refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    document.title = "Edit Webhook";
    if (subscriberData) {
      setFormData(subscriberData);
      if (subscriberData.customHeader)
        handleHeaderParse(subscriberData.customHeader);

      // No stored selection means the subscriber receives every payment type.
      setSelectedPaymentTypes(
        subscriberData.paymentTypes?.length
          ? subscriberData.paymentTypes
          : [ALL_PAYMENT_TYPES]
      );


      if (dealersByMerchantData) {
        setDealersList(dealersByMerchantData);

        setSelectedDealers(
          dealersByMerchantData.filter((x) =>
            subscriberData?.dealerIds?.includes(x.dealerId)
          )
        );
        setIsDealerDisabled(false);
      }
    }
  }, [dealersByMerchantData, isFetching, subscriberData, isSubscriberFetching]);

  const handleChange = (field: string) => (event: any) => {
    const value =
      field === "isActive" ? event.target.checked : event.target.value;
    if (field === "endpointUrl")
      setIsValidEndPoint(value !== "" && !validateUrl(value));
    if (field === "deadLetterUrl")
      setIsValidDeadUrl(value !== "" && !validateUrl(value));
    if (field === "endSystemOwnerEmail")
      setEmailError(!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value));
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSecurityChange = (field: string) => (event: any) => {
    const value = event.target.value;
    setSecretData((prev) => ({ ...prev, [field]: value }));
  };

  const performHealthCheckPing = async () => {
    const payload: performHealthCheckRequest = {
      endpointUrl: formData?.endpointUrl,
      triggeredBy: "Manual",
    };
    try {
      setIsHealthCheckProcessing(true);
      const response = await performHealthCheck(payload).unwrap();
      if (response !== undefined && response.pingStatus === "Success") {
        toast.success(response?.message);
        setHealthCheckId(response?.healthCheckId);
        setHealthCheckedURL(payload.endpointUrl);
      } else if (response !== undefined && response.pingStatus === "Failed") {
        toast.error(response?.message);
      } else {
        toast.error("An unexpected error occurred.");
      }
    } catch (err) {
      handleApiError(err);
    }
    setIsHealthCheckProcessing(false);
  };

  const createWebHookSecret = async () => {
    const payload: CreateWebHookSecretRequest = {
      securityType: secretData?.securityType,
      userName: secretData?.userName ? secretData?.userName : null,
      password: secretData?.password ? secretData?.password : null,
    };
    try {
      setIsSecretProcessing(true);
      const response = await createSecret(payload).unwrap();
      if (response !== undefined && response.id) {
        setSecretId(response?.id);
        if (secretData?.securityType === "HMAC") {
          toast.success("HMAC generated Successfully!");
          setSecretCode(response?.secret);
        } else {
          toast.success("Credentials locked Successfully!");
        }
      } else {
        toast.error("An unexpected error occurred.");
      }
    } catch (err) {
      handleApiError(err);
    }
    setIsSecretProcessing(false);
  };

  function validateForm(): boolean {
    if (!formData?.description) {
      toast.error("Invalid input. Missing Description!");
      return false;
    }
    if (
      subscriberData?.dealerIds?.length > 0 &&
      (!selectedDealers || selectedDealers.length === 0)
    ) {
      toast.error(
        "No dealers selected. Dealer-level configurations require at least one dealer.",
      );
      return false;
    }
    if (!formData?.endpointUrl || isValidEndPoint) {
      toast.error("Invalid input. Missing Endpoint URL!");
      return false;
    }
    if (
      (formData?.endpointUrl !== subscriberData?.endpointUrl &&
        !healthCheckId) ||
      (formData?.endpointUrl !== healthCheckedURL && healthCheckId)
    ) {
      toast.error("Action required. Please perform the health check!");
      return false;
    }
    if (!formData?.preferredHttpMethod) {
      toast.error("Invalid input. Missing Http Method!");
      return false;
    }
    if (!formData?.deliveryFormat) {
      toast.error("Invalid input. Missing delivery format!");
      return false;
    }
    if (secretData?.securityType != null && !secretId) {
      toast.error(
        "Action required. Please lock the secret or generate the code!",
      );
      return false;
    }
    if (!formData?.endSystemOwnerEmail || emailError) {
      toast.error("Invalid input. Missing email!");
      return false;
    }
    return true;
  }

  /**
   * The filter only has meaning for subscribers that receive payment events; a REFUND-only
   * subscriber is never filtered, so the picker is hidden rather than shown with no effect.
   * Event types cannot be changed on this page, so this is fixed for the life of the form.
   */
  const receivesPaymentEvents = (subscriberData?.eventTypes ?? []).some((eventType) =>
    eventType?.toUpperCase().startsWith("PAYMENT.")
  );

  /**
   * Keeps ALL mutually exclusive with the specific payment types, and never leaves the selection
   * empty — the server rejects an empty list, and "no filter" is expressed as ALL.
   */
  const handlePaymentTypeSelection = (value: string[]) => {
    if (!value || value.length === 0) {
      setSelectedPaymentTypes([ALL_PAYMENT_TYPES]);
      return;
    }

    const allWasJustPicked = value[value.length - 1] === ALL_PAYMENT_TYPES;
    setSelectedPaymentTypes(
      allWasJustPicked
        ? [ALL_PAYMENT_TYPES]
        : value.filter((type) => type !== ALL_PAYMENT_TYPES)
    );
  };

  const handleSubmission = async () => {
    try {
      setIsProcessing(true);
      if (validateForm()) {
        const headerString = rows
          .filter((kvp) => kvp.key && kvp.value)
          .map((kvp) => `${kvp.key}:${kvp.value}`)
          .join(",");

        const payload = {
          ...formData,
          ["dealerIds"]: selectedDealers?.map((item) => item.dealerId),
          // Omitted when the picker is hidden, so the server leaves any stored selection alone
          // rather than this page silently resetting it to ALL.
          ...(receivesPaymentEvents
            ? { paymentTypes: selectedPaymentTypes }
            : {}),
          ["healthCheckId"]: healthCheckId ? healthCheckId : null,
          ["secretId"]: secretId ? secretId : null,
          ["customHeader"]: headerString,
        };

        const response = await submitForm(payload).unwrap();
        if (response !== undefined && response.id) {
          toast.success(`WebHook modified successfully!`);
          setTimeout(() => navigate("/dashboard/manageWebHooks"), 2000);
        } else {
          toast.error("Failed to process the WebHook.");
        }
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      sx={{ mt: 4 }}
    >
      {allowWebhookIntegration === false && <Navigate to="/401" />}
      <Card elevation={3} sx={{ width: 680, p: 4, borderRadius: 3 }}>
        <Typography
          variant="h6"
          sx={{ color: "primary.main", mb: 3, fontWeight: 600 }}
        >
          Edit WebHook
          {(isSubscriberFetching ||
            (subscriberData?.dealerIds && isFetching)) && (
            <CircularProgress
              size={25}
              style={{ marginLeft: "20px", float: "right" }}
              color="info"
            />
          )}
        </Typography>

        {subscriberData && (
          <>
            {/* Webhook Details (read-only) */}
            <Accordion defaultExpanded sx={{ mb: 3, borderRadius: 3 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                style={{ backgroundColor: "#f9f9f9" }}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  Webhook Detail
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <AccordionDetails>
                  <Table>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", width: "290px" }}>
                        Webhook ID
                      </TableCell>
                      <TableCell valign="middle" sx={{ width: "290px" }}>
                        {subscriberData?.id}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", width: "290px" }}>
                        Merchant
                      </TableCell>
                      <TableCell sx={{ width: "290px" }}>
                        {subscriberData?.merchantId}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", width: "290px" }}>
                        Events
                      </TableCell>
                      <TableCell sx={{ width: "290px" }}>
                        {subscriberData?.eventTypes?.join(", ")}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", width: "290px" }}>
                        Created Date
                      </TableCell>
                      <TableCell sx={{ width: "290px" }}>
                        {subscriberData?.createdDate?.toUSDateFormat()}
                      </TableCell>
                    </TableRow>
                  </Table>
                </AccordionDetails>
              </AccordionDetails>
            </Accordion>

            {/* General */}
            <Accordion sx={{ mb: 3, borderRadius: 3 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                style={{ backgroundColor: "#f9f9f9" }}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  General
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormControlLabel
                  control={
                    <Switch
                      value={formData?.isActive}
                      checked={formData?.isActive}
                      onChange={handleChange("isActive")}
                      color="primary"
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center">
                      Enabled?
                      <Tooltip title="Enable or disable this webhook">
                        <IconButton size="small">
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                />

                <Box style={formStyles.fieldSpacer}>
                  <Autocomplete
                    fullWidth
                    multiple
                    id="combo-box-demo"
                    disabled={isDealerDisabled}
                    disableCloseOnSelect
                    options={dealersList}
                    value={selectedDealers || []}
                    getOptionLabel={(option: any) =>
                      `${option.dealerName} - ${option.dealerId}`
                    }
                    onChange={(event, value) =>
                      setSelectedDealers(value ?? [])
                    }
                    renderOption={(props, option, { selected }) => {
                      const isDisabled = selectedDealers.some(
                        (element) =>
                          element.dealerId === option.dealerId &&
                          !isEmpty(element.requestedDate)
                      );
                      return (
                        <li
                          {...props}
                          style={{
                            pointerEvents: isDisabled ? "none" : "auto",
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                style={{ padding: "2px 5px" }}
                                checked={selected}
                              />
                            }
                            label={`${option.dealerName} - ${option.dealerId}`}
                          />
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        label="Select Dealers"
                        InputProps={{ ...params.InputProps, endAdornment: "" }}
                        inputProps={{ ...params.inputProps }}
                      />
                    )}
                    isOptionEqualToValue={(option, value) =>
                      option.dealerId === value.dealerId
                    }
                    renderTags={(selected, getTagProps) =>
                      selected.map((option, index) => {
                        const isDisabled = selectedDealers.some(
                          (element) =>
                            element.dealerId === option.dealerId &&
                            !isEmpty(element.requestedDate)
                        );
                        return (
                          <Chip
                            key={option.dealerId}
                            label={`${option.dealerName} - ${option.dealerId}`}
                            {...getTagProps({ index })}
                            disabled={isDisabled}
                            style={{
                              pointerEvents: isDisabled ? "none" : "auto",
                              color: isDisabled ? "gray" : "inherit",
                            }}
                          />
                        );
                      })
                    }
                  />
                </Box>

                {receivesPaymentEvents && (
                <Box style={formStyles.fieldSpacer}>
                  <Autocomplete
                    fullWidth
                    multiple
                    id="payment-types"
                    disableCloseOnSelect
                    options={PAYMENT_TYPE_OPTIONS}
                    value={selectedPaymentTypes}
                    onChange={(event, value) =>
                      handlePaymentTypeSelection(value as string[])
                    }
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              style={{ padding: "2px 5px" }}
                              checked={selected}
                            />
                          }
                          label={option}
                        />
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        label="Payment Types"
                        helperText="ALL delivers every payment type. Choose specific types to receive only those payment events."
                        InputProps={{ ...params.InputProps, endAdornment: "" }}
                        inputProps={{ ...params.inputProps }}
                      />
                    )}
                    renderTags={(selected, getTagProps) =>
                      selected.map((option, index) => (
                        <Chip
                          key={option}
                          label={option}
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                  />
                </Box>
                )}

                <TextField
                  sx={{ mb: 3 }}
                  label="Description *"
                  fullWidth
                  multiline
                  margin="normal"
                  variant="standard"
                  value={formData?.description}
                  onChange={handleChange("description")}
                />
              </AccordionDetails>
            </Accordion>

            {/* Server Config */}
            <Accordion sx={{ mb: 3, borderRadius: 3 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                style={{ backgroundColor: "#f9f9f9" }}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  Server Configurations
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TextField
                  variant="standard"
                  label="Endpoint URL *"
                  value={formData?.endpointUrl}
                  onChange={handleChange("endpointUrl")}
                  error={isValidEndPoint}
                  helperText={isValidEndPoint ? "Enter a valid URL" : ""}
                  fullWidth
                  margin="normal"
                  type="url"
                />
                <Box>
                  <Button
                    style={{ float: "right" }}
                    variant="outlined"
                    onClick={performHealthCheckPing}
                    disabled={
                      isValidEndPoint ||
                      !formData?.endpointUrl ||
                      formData?.endpointUrl == subscriberData?.endpointUrl ||
                      formData?.endpointUrl == healthCheckedURL
                    }
                  >
                    {isHealthCheckProcessing ? (
                      <CircularProgress size={25} color="info" />
                    ) : (
                      "Health Test"
                    )}
                  </Button>
                </Box>
                <FormControl variant="standard" fullWidth margin="normal">
                  <InputLabel>Http Method *</InputLabel>
                  <Select
                    value={formData?.preferredHttpMethod || ""}
                    onChange={handleChange("preferredHttpMethod")}
                  >
                    <MenuItem value="POST">POST</MenuItem>
                    <MenuItem value="PUT">PUT</MenuItem>
                  </Select>
                </FormControl>
                <FormControl variant="standard" fullWidth margin="normal">
                  <InputLabel>Delivery Format *</InputLabel>
                  <Select
                    value={formData?.deliveryFormat || ""}
                    onChange={handleChange("deliveryFormat")}
                  >
                    <MenuItem value="json">JSON</MenuItem>
                  </Select>
                </FormControl>
              </AccordionDetails>
            </Accordion>

            {/* Re-generate Security */}
            <Accordion sx={{ mb: 3, borderRadius: 3 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                style={{ backgroundColor: "#f9f9f9" }}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  Re-generate Security
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormControl variant="standard" fullWidth margin="normal">
                  <InputLabel>Security Type</InputLabel>
                  <Select
                    value={secretData?.securityType}
                    onChange={handleSecurityChange("securityType")}
                    disabled={secretId}
                  >
                    <MenuItem value="Basic">Basic</MenuItem>
                    <MenuItem value="HMAC">HMAC</MenuItem>
                  </Select>
                </FormControl>
                {secretData?.securityType == "Basic" && (
                  <>
                    <TextField
                      value={secretData?.userName}
                      variant="standard"
                      label="Username"
                      fullWidth
                      margin="normal"
                      onChange={handleSecurityChange("userName")}
                      disabled={secretId}
                      InputProps={{
                        endAdornment: secretId ? (
                          <InputAdornment position="end">
                            <LockIcon color="disabled" />
                          </InputAdornment>
                        ) : null,
                      }}
                    />
                    <TextField
                      value={secretData?.password}
                      variant="standard"
                      label="Password"
                      type="password"
                      fullWidth
                      margin="normal"
                      onChange={handleSecurityChange("password")}
                      disabled={secretId}
                      InputProps={{
                        endAdornment: secretId ? (
                          <InputAdornment position="end">
                            <LockIcon color="disabled" />
                          </InputAdornment>
                        ) : null,
                      }}
                    />
                    <Box>
                      <Button
                        style={{
                          float: "right",
                          marginBottom: 20,
                          marginTop: 10,
                        }}
                        variant="outlined"
                        onClick={createWebHookSecret}
                        disabled={
                          !secretData?.userName ||
                          !secretData?.password ||
                          secretId
                        }
                      >
                        {isSecretProcessing ? (
                          <CircularProgress size={25} color="info" />
                        ) : (
                          "Lock Credentials"
                        )}
                      </Button>
                    </Box>
                  </>
                )}
                {secretData?.securityType == "HMAC" && (
                  <>
                    <TextField
                      value={secretCode || ""}
                      variant="standard"
                      label="HMAC Code"
                      fullWidth
                      margin="normal"
                      disabled
                      InputProps={{
                        endAdornment: secretCode ? (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => copyText(secretCode)}
                              edge="end"
                            >
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                      }}
                    />
                    <Box>
                      <Button
                        style={{
                          float: "right",
                          marginBottom: 20,
                          marginTop: 10,
                        }}
                        variant="outlined"
                        onClick={createWebHookSecret}
                        disabled={secretId}
                      >
                        {isSecretProcessing ? (
                          <CircularProgress size={25} color="info" />
                        ) : (
                          "Generate"
                        )}
                      </Button>
                    </Box>
                  </>
                )}
              </AccordionDetails>
            </Accordion>

            {/* Additional Settings */}
            <Accordion sx={{ mb: 3, borderRadius: 3 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                style={{ backgroundColor: "#f9f9f9" }}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  Additional Settings
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormControl variant="standard" fullWidth margin="normal">
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    Add Custom Headers (KVP)
                  </Typography>
                  {rows.map((row, index) => (
                    <Grid
                      container
                      spacing={2}
                      alignItems="center"
                      key={index}
                      sx={{ mb: 1 }}
                    >
                      <Grid item xs={5}>
                        <TextField
                          label="Key"
                          variant="outlined"
                          fullWidth
                          value={row.key}
                          onChange={(e) =>
                            handleKVPChange(index, "key", e.target.value)
                          }
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="Value"
                          variant="outlined"
                          fullWidth
                          value={row.value}
                          onChange={(e) =>
                            handleKVPChange(index, "value", e.target.value)
                          }
                        />
                      </Grid>
                      <Grid item xs={1}>
                        <Box display="flex" justifyContent="center">
                          {index === rows.length - 1 ? (
                            <IconButton color="primary" onClick={handleAddRow}>
                              <AddCircle />
                            </IconButton>
                          ) : (
                            <IconButton
                              color="error"
                              onClick={() => handleRemoveRow(index)}
                            >
                              <Delete />
                            </IconButton>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  ))}
                </FormControl>
                <TextField
                  value={formData?.endSystemOwnerEmail}
                  variant="standard"
                  label="System Owner Email *"
                  type="email"
                  fullWidth
                  margin="normal"
                  onChange={handleChange("endSystemOwnerEmail")}
                  error={emailError}
                  helperText={
                    emailError ? "Please enter a valid email address" : ""
                  }
                />
              </AccordionDetails>
            </Accordion>

            {/* Save Button */}
            <Box style={{ padding: "10px 10px 10px 0px", textAlign: "left" }}>
              <Button
                style={formStyles.fieldSpacer}
                variant="contained"
                color="primary"
                onClick={handleSubmission}
                disabled={
                  !formData?.description ||
                  !formData?.endpointUrl ||
                  !formData?.endSystemOwnerEmail
                }
              >
                Update
              </Button>
              {isProcessing && (
                <CircularProgress
                  size={25}
                  style={{ marginLeft: "20px" }}
                  color="info"
                />
              )}
            </Box>
          </>
        )}
      </Card>
    </Box>
  );
}
